import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Construction, CornerDownRight, Lock, MessageSquareWarning, ShieldAlert } from "lucide-react";
import { ACCESS_TEMPLATES, ACCESS_VERBS, DATA_REGIONS, MENUS, accessTemplate, type AccessVerb, type DataRegion } from "@hisense/shared";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { useQuotationCustomerOptions, useSuppliers } from "@/api/hooks";
import { useSetUserAccess, useUserAccess, type UserRow } from "@/api/hooks";
import { getLocalizedError } from "@/lib/api";
import { useAuth } from "@/app/auth";

type Grants = Record<string, AccessVerb[]>;

// Trang đã ẩn khỏi giao diện thì không vẽ dòng tick — cấp quyền vào chỗ không ai thấy chỉ gây hiểu nhầm.
const VISIBLE_MENUS = MENUS.filter((m) => !m.hidden);
const GROUPS = [...new Set(VISIBLE_MENUS.map((m) => m.group))];

/**
 * Bảng phân quyền của MỘT tài khoản: tick từng màn hình, từng hành động, và
 * gán danh sách khách.
 *
 * Mount kèm `key={user.id}` — trạng thái form không được rớt từ người này sang
 * người khác, đúng quy tắc của mọi panel sửa trong hệ này.
 */
export function UserAccessPanel({ user, onClose }: { user: UserRow; onClose: () => void }) {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const access = useUserAccess(user.id);
  const save = useSetUserAccess();
  // TOÀN BỘ khách, xếp theo tên — nạp đủ mọi trang. Trước đây hỏi một trang 500 dòng,
  // vượt trần 200 của API nên bị trả 422 và danh sách khách trống trơn.
  const customers = useQuotationCustomerOptions();
  // Nhà cung cấp riêng tư (28/10/2026): hộp này chỉ quản trị viên mở, và quản trị viên thấy mọi nhà cung cấp (≤ 200, 26 trên production).
  const suppliers = useSuppliers({ pageSize: 200 });

  const [mode, setMode] = useState<"role" | "custom" | null>(null);
  const [grants, setGrants] = useState<Grants | null>(null);
  const [scope, setScope] = useState<"all" | "assigned" | null>(null);
  const [customerIds, setCustomerIds] = useState<string[] | null>(null);
  const [supplierIds, setSupplierIds] = useState<string[] | null>(null);
  const [cost, setCost] = useState<boolean | null>(null);
  const [region, setRegion] = useState<DataRegion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customerQuery, setCustomerQuery] = useState("");

  const loaded = access.data;
  const curMode = mode ?? loaded?.mode ?? "role";
  const curGrants = grants ?? loaded?.grants ?? {};
  const curScope = scope ?? loaded?.customerScope ?? "all";
  const curCustomers = customerIds ?? loaded?.customerIds ?? [];
  const curSuppliers = supplierIds ?? loaded?.supplierIds ?? [];
  const curCost = cost ?? loaded?.canViewCost ?? false;
  const curRegion = region ?? loaded?.region ?? "all";

  const isAdmin = user.role === "admin";
  const isSelf = me?.id === user.id;
  const locked = isAdmin || isSelf;

  /*
   * Có khu vực thì danh sách khách chỉ gồm khách của khu vực đó: khách ngoài khu vực đằng nào cũng bị ẩn với tài khoản này, tick
   * vào chỉ làm con số "đã chọn" nói sai. Khách đã tick từ trước mà nằm ngoài khu vực bị bỏ khi lưu, và không được đếm.
   *
   * Khách đã xoá (không còn trong danh sách) thì GIỮ khi lưu — chứng từ cũ của khách đó vẫn theo người theo đơn — nhưng không đếm:
   * con số "đã chọn" phải khớp với số ô đang tick trên màn hình (29/09/2026).
   */
  const allIds = useMemo(() => new Set((customers.data ?? []).map((r) => r.id)), [customers.data]);
  const inRegion = useMemo(() => {
    if (curRegion === "all") return null;
    return new Set((customers.data ?? []).filter((r) => r.classification === curRegion).map((r) => r.id));
  }, [customers.data, curRegion]);
  const chosen = inRegion && customers.data ? curCustomers.filter((id) => !allIds.has(id) || inRegion.has(id)) : curCustomers;
  const chosenCount = customers.data ? chosen.filter((id) => allIds.has(id)).length : chosen.length;

  /*
   * Nhà cung cấp RIÊNG TƯ người này được thấy — cùng danh sách với ô Người theo dõi trên hồ sơ nhà cung cấp. Nhà cung cấp thường
   * không có ở đây: cả đội đều thấy. Có khu vực thì chỉ nhà cung cấp của khu vực đó và nhà cung cấp chưa phân đội.
   */
  const privateSuppliers = useMemo(
    () => (suppliers.data?.data ?? []).filter((s) => s.isPrivate && (curRegion === "all" || !s.classification || s.classification === curRegion)),
    [suppliers.data, curRegion],
  );

  const customerRows = useMemo(() => {
    const rows = (customers.data ?? []).filter((r) => !inRegion || inRegion.has(r.id));
    const q = customerQuery.trim().toLowerCase();
    return q ? rows.filter((r) => r.companyName.toLowerCase().includes(q)) : rows;
  }, [customers.data, customerQuery, inRegion]);

  const toggle = (menu: string, verb: AccessVerb) => {
    const now = curGrants[menu] ?? [];
    const has = now.includes(verb);
    let next = has ? now.filter((v) => v !== verb) : [...now, verb];
    // Bỏ "Xem" là bỏ cả màn hình: sửa hay xoá thứ mình không được nhìn thấy là
    // một trạng thái vô nghĩa, để nó tồn tại chỉ làm người quản trị hiểu nhầm.
    if (verb === "view" && has) next = [];
    if (verb !== "view" && !has && !next.includes("view")) next.push("view");
    const out: Grants = { ...curGrants, [menu]: next.length ? (ACCESS_VERBS.filter((v) => next.includes(v)) as AccessVerb[]) : [] };
    // Dòng quyền theo phần (vd Điều khoản & yêu cầu đặt hàng dưới Khách hàng): tick con thì cha phải có Xem; bỏ Xem của cha là bỏ cả con.
    const parent = MENUS.find((m) => m.key === menu)?.parent;
    if (parent && next.length && !(out[parent] ?? []).includes("view")) out[parent] = ["view", ...(out[parent] ?? [])];
    if (!next.length) for (const child of MENUS.filter((m) => m.parent === menu)) out[child.key] = [];
    setGrants(out);
  };

  const setAll = (menu: string, on: boolean) => {
    const def = MENUS.find((m) => m.key === menu);
    const out: Grants = { ...curGrants, [menu]: on ? [...(def?.verbs ?? ACCESS_VERBS)] : [] };
    if (on && def?.parent && !(out[def.parent] ?? []).includes("view")) out[def.parent] = ["view", ...(out[def.parent] ?? [])];
    if (!on) for (const child of MENUS.filter((m) => m.parent === menu)) out[child.key] = [];
    setGrants(out);
  };

  /**
   * Đổ một bộ mẫu theo vị trí công việc — ĐIỂM XUẤT PHÁT, không phải khoá.
   *
   * Mẫu mang theo cả ô giá vốn và phạm vi khách (nhân viên theo đơn mặc định
   * "chỉ khách mình theo đơn"); danh sách khách cụ thể thì vẫn phải chọn tay
   * (hoặc chọn người theo đơn trên hồ sơ khách), vì không mẫu nào biết ai theo
   * khách nào.
   */
  const applyTemplate = (key: string) => {
    const tpl = accessTemplate(key);
    if (!tpl) return;
    const next: Grants = {};
    for (const [menu, verbs] of Object.entries(tpl.grants)) next[menu] = [...verbs];
    setGrants(next);
    setCost(tpl.canViewCost);
    setScope(tpl.customerScope);
  };

  const submit = async () => {
    setError(null);
    try {
      await save.mutateAsync({
        id: user.id,
        mode: curMode,
        customerScope: curScope,
        canViewCost: curCost,
        grants: curGrants,
        customerIds: chosen,
        region: curRegion,
        supplierIds: curSuppliers,
      });
      onClose();
    } catch (err) {
      setError(getLocalizedError(err, t, "userAccess.title"));
    }
  };

  return (
    <Modal
      eyebrow={user.email}
      title={`${t("userAccess.title")} — ${user.displayName}`}
      closeLabel={t("common.cancel")}
      onClose={() => { if (!save.isPending) onClose(); }}
      className="max-w-4xl"
    >
      <div className="space-y-5 overflow-y-auto px-5 py-4 text-sm">
        {locked ? (
          <p className="flex items-start gap-2 rounded-md border border-warn/40 bg-warn/10 p-3 text-warn-fg">
            <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>{isAdmin ? t("userAccess.adminNote") : t("userAccess.selfNote")}</span>
          </p>
        ) : null}

        <fieldset disabled={locked} className="space-y-3 disabled:opacity-60">
          <FieldLabel>{t("userAccess.mode")}</FieldLabel>
          <div className="grid gap-2 sm:grid-cols-2">
            {(["role", "custom"] as const).map((m) => (
              <label
                key={m}
                className={`flex cursor-pointer gap-2 rounded-md border p-3 ${curMode === m ? "border-brand bg-brand/5" : "border-line"}`}
              >
                <input type="radio" name="access-mode" checked={curMode === m} onChange={() => setMode(m)} className="mt-1" />
                <span>
                  <span className="block font-medium">{t(m === "role" ? "userAccess.modeRole" : "userAccess.modeCustom")}</span>
                  <span className="block text-xs text-muted">
                    {t(m === "role" ? "userAccess.modeRoleHint" : "userAccess.modeCustomHint", { role: t(`roles.${user.role}`) })}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset disabled={locked} className="space-y-2 disabled:opacity-60">
          <FieldLabel>{t("userAccess.region")}</FieldLabel>
          <p className="text-xs text-muted">{t("userAccess.regionHint")}</p>
          <div className="grid gap-2 sm:grid-cols-3">
            {DATA_REGIONS.map((r) => (
              <label
                key={r}
                className={`flex cursor-pointer items-center gap-2 rounded-md border p-3 ${curRegion === r ? "border-brand bg-brand/5" : "border-line"}`}
              >
                <input type="radio" name="data-region" checked={curRegion === r} onChange={() => setRegion(r)} />
                <span className="font-medium">
                  {t(r === "all" ? "userAccess.regionAll" : r === "vietnam" ? "userAccess.regionVietnam" : "userAccess.regionChina")}
                </span>
              </label>
            ))}
          </div>
          {curRegion !== "all" && !locked ? (
            <>
              <p className="text-xs text-muted">{t("userAccess.regionOneHint")}</p>
              <p className="flex items-start gap-2 rounded-md border border-warn/40 bg-warn/10 p-2 text-xs text-warn-fg">
                <MessageSquareWarning className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>
                  {/* Dấu câu nằm trong chuỗi dịch: tiếng Hoa dùng "：", không phải ": ". */}
                  <span className="font-medium">{t("userAccess.regionPending")}</span>
                  {t("userAccess.regionPendingHint")}
                </span>
              </p>
            </>
          ) : null}
        </fieldset>

        {/*
          * Khách tài khoản này được xem (29/09/2026) — cho CẢ HAI chế độ, như khu vực: nhân viên theo đơn chạy theo vai trò cũng
          * giới hạn được mà không phải tick lại từng màn hình. Danh sách bên dưới chính là ô Người theo đơn trên hồ sơ từng khách.
          */}
        {!locked ? (
          <section className="space-y-2">
            <FieldLabel>{t("userAccess.customers")}</FieldLabel>
            <div className="space-y-1">
              {(["all", "assigned"] as const).map((s) => (
                <label key={s} className="flex items-start gap-2">
                  <input type="radio" name="customer-scope" checked={curScope === s} onChange={() => setScope(s)} className="mt-1" />
                  <span>
                    <span className="block">{t(s === "all" ? "userAccess.customerAll" : "userAccess.customerAssigned")}</span>
                    <span className="block text-xs text-muted">{t(s === "all" ? "userAccess.customerAllHint" : "userAccess.customerAssignedHint")}</span>
                  </span>
                </label>
              ))}
            </div>

            {curScope === "assigned" ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-ink">{t("userAccess.customerFollowedList")}</p>
                <div className="flex items-center gap-2">
                  <Input
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    placeholder={t("userAccess.customerSearch")}
                    aria-label={t("userAccess.customerSearch")}
                  />
                  <Badge tone="info">{t("userAccess.customerSelected", { count: chosenCount })}</Badge>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-md border border-line">
                  {customers.isPending ? (
                    <p className="px-3 py-2 text-sm text-muted">{t("common.loading")}</p>
                  ) : customers.isError ? (
                    <div role="alert" className="flex items-center gap-2 px-3 py-2 text-sm text-danger">
                      <span>{t("userAccess.customersError")}</span>
                      <Button size="sm" variant="ghost" onClick={() => void customers.refetch()}>
                        {t("common.retry")}
                      </Button>
                    </div>
                  ) : customerRows.length === 0 ? (
                    <p className="px-3 py-2 text-sm text-muted">{t("common.noMatches")}</p>
                  ) : customerRows.map((row) => (
                    <label key={row.id} className="flex items-center gap-2 border-b border-line px-3 py-1.5 last:border-b-0">
                      <input
                        type="checkbox"
                        checked={curCustomers.includes(row.id)}
                        onChange={(e) =>
                          setCustomerIds(
                            e.target.checked ? [...curCustomers, row.id] : curCustomers.filter((id) => id !== row.id),
                          )
                        }
                      />
                      <span>{row.companyName}</span>
                      {/* Khách riêng tư (30/09/2026): gán cho người theo đơn cũng không làm họ thấy — chỉ quản trị viên thấy. */}
                      {row.isPrivate ? (
                        <Badge tone="neutral" title={t("customers.privateHint")}>
                          <Lock className="size-3" aria-hidden="true" /> {t("customers.privateBadge")}
                        </Badge>
                      ) : null}
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {!locked ? (
          <section className="space-y-2">
            <FieldLabel>{t("userAccess.privateSuppliers")}</FieldLabel>
            <p className="text-xs text-muted">{t("userAccess.privateSuppliersHint")}</p>
            <div className="max-h-48 overflow-y-auto rounded-md border border-line">
              {suppliers.isPending ? (
                <p className="px-3 py-2 text-sm text-muted">{t("common.loading")}</p>
              ) : suppliers.isError ? (
                <div role="alert" className="flex items-center gap-2 px-3 py-2 text-sm text-danger">
                  <span>{t("userAccess.privateSuppliersError")}</span>
                  <Button size="sm" variant="ghost" onClick={() => void suppliers.refetch()}>
                    {t("common.retry")}
                  </Button>
                </div>
              ) : privateSuppliers.length === 0 ? (
                <p className="px-3 py-2 text-sm text-muted">{t("userAccess.privateSuppliersNone")}</p>
              ) : privateSuppliers.map((row) => (
                <label key={row.id} className="flex items-center gap-2 border-b border-line px-3 py-1.5 last:border-b-0">
                  <input
                    type="checkbox"
                    checked={curSuppliers.includes(row.id)}
                    onChange={(e) =>
                      setSupplierIds(e.target.checked ? [...curSuppliers, row.id] : curSuppliers.filter((id) => id !== row.id))
                    }
                  />
                  <span>{row.companyName}</span>
                  <Badge tone="neutral" title={t("suppliers.privateHint")}>
                    <Lock className="size-3" aria-hidden="true" /> {t("suppliers.privateBadge")}
                  </Badge>
                </label>
              ))}
            </div>
          </section>
        ) : null}

        {curMode === "custom" && !locked ? (
          <>
            <section className="space-y-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <FieldLabel>{t("userAccess.screens")}</FieldLabel>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className="h-8 rounded-md border border-line bg-surface px-2 text-xs"
                    aria-label={t("userAccess.useTemplate")}
                    value=""
                    onChange={(e) => { if (e.target.value) applyTemplate(e.target.value); }}
                  >
                    <option value="">{t("userAccess.useTemplate")}</option>
                    {ACCESS_TEMPLATES.map((tpl) => (
                      <option key={tpl.key} value={tpl.key}>
                        {t(`userAccess.templates.${tpl.key}`)}
                      </option>
                    ))}
                  </select>
                  <Button size="sm" variant="ghost" onClick={() => setGrants(loaded?.preset ?? {})}>
                    {t("userAccess.usePreset", { role: t(`roles.${user.role}`) })}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted">{t("userAccess.screensHint")}</p>
              <p className="text-xs text-muted">{t("userAccess.approveHint")}</p>
              <p className="text-xs text-muted">{t("userAccess.exportNote")}</p>

              <div className="max-h-[45vh] overflow-y-auto rounded-md border border-line">
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-surface-2 text-xs">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t("userAccess.screens")}</th>
                      {ACCESS_VERBS.map((v) => (
                        <th key={v} className="w-16 px-2 py-2 text-center font-medium">
                          {t(`userAccess.verbs.${v}`)}
                        </th>
                      ))}
                      <th className="w-24 px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {GROUPS.map((group) => (
                      <Fragment key={group}>
                        <tr className="bg-surface-2/60">
                          <td colSpan={ACCESS_VERBS.length + 2} className="px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted">
                            {t(`nav.${group}`)}
                          </td>
                        </tr>
                        {VISIBLE_MENUS.filter((m) => m.group === group).map((menu) => {
                          const verbs = curGrants[menu.key] ?? [];
                          const allowed = menu.verbs ?? ACCESS_VERBS;
                          return (
                            <tr key={menu.key} className="border-t border-line">
                              <td className={menu.parent ? "py-1.5 pl-7 pr-3" : "px-3 py-1.5"}>
                                {menu.parent && <CornerDownRight className="mr-1 inline size-3.5 text-ink-faint" aria-hidden="true" />}
                                {t(`nav.${menu.key}`)}
                                {/* Dòng mới 04/11/2026 (mục 8) — dấu "Đang làm" tới khi khách nghiệm thu. */}
                                {(menu.key === "customerTerms" || menu.key === "redPi") && (
                                  <Badge tone="info" outline className="ml-2 align-middle text-[10px]">
                                    <Construction className="mr-1 inline size-3" aria-hidden="true" />
                                    {t("nav.inProgress")}
                                  </Badge>
                                )}
                                {menu.customerScoped ? (
                                  <Badge tone="neutral" outline className="ml-2 align-middle text-[10px]">
                                    {t("userAccess.scopedBadge")}
                                  </Badge>
                                ) : null}
                              </td>
                              {ACCESS_VERBS.map((v) => (
                                <td key={v} className="px-2 py-1.5 text-center">
                                  {allowed.includes(v) ? (
                                    <input
                                      type="checkbox"
                                      aria-label={`${t(`nav.${menu.key}`)} — ${t(`userAccess.verbs.${v}`)}`}
                                      checked={verbs.includes(v)}
                                      onChange={() => toggle(menu.key, v)}
                                    />
                                  ) : null}
                                </td>
                              ))}
                              <td className="px-2 py-1.5 text-right">
                                <button type="button" className="text-xs text-brand hover:underline" onClick={() => setAll(menu.key, verbs.length < allowed.length)}>
                                  {verbs.length >= allowed.length ? t("userAccess.none") : t("userAccess.all")}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <label className="flex items-start gap-2">
              <input type="checkbox" checked={curCost} onChange={(e) => setCost(e.target.checked)} className="mt-1" />
              <span>
                <span className="block font-medium">{t("userAccess.cost")}</span>
                <span className="block text-xs text-muted">{t("userAccess.costHint")}</span>
              </span>
            </label>

          </>
        ) : null}

        {error ? <p className="text-sm text-danger">{error}</p> : null}
        <p className="text-xs text-muted">{t("userAccess.signOutWarning")}</p>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} disabled={locked || save.isPending}>
            {t("userAccess.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
